import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    StyleSheet,
    View,
    Platform,
    Linking,
    Animated,
    Easing
} from 'react-native';
import getScreenType from '../../../utils/getScreenType';
import { useTheme } from '@emotion/react';
import WebView from 'react-native-webview';
import generatePartnerUrl from '../../../utils/functions';

interface ODSHomeIframeProps {
    fullWidth?: boolean;
    navigation?: any;
    iFrameParams?: any;
    homeBaseUrl?: string;
    onPress?: () => void;
}

const ODSHomeIframe = React.memo(({
    fullWidth = false,
    navigation,
    iFrameParams,
    homeBaseUrl,
    onPress
}: ODSHomeIframeProps) => {
    const [loading, setLoading] = useState(true);
    const theme = useTheme() as any;
    const shimmerAnimation = useRef(new Animated.Value(0)).current;
    const iframeRef = useRef<any>(null);

    // Animation for the shimmer effect
    useEffect(() => {
        let isMounted = true;
        const startShimmerAnimation = () => {
            if (!isMounted) return;
            shimmerAnimation.setValue(0);
            Animated.timing(shimmerAnimation, {
                toValue: 1,
                duration: 1500,
                easing: Easing.linear,
                useNativeDriver: Platform.OS !== 'web',
            }).start(() => {
                if (loading && isMounted) {
                    startShimmerAnimation();
                }
            });
        };

        if (loading) {
            startShimmerAnimation();
        }
        return () => { isMounted = false; };
    }, [loading, shimmerAnimation]);

    const handleWebViewMessage = useCallback((event: any) => {
        const { data } = event.nativeEvent;
        try {
            const fromWeb = JSON.parse(data);
            console.log('fromWeb', fromWeb);
            if (fromWeb?.type === 'iframe_click') {
                onPress?.();
            }
            if (fromWeb?.val?.type && fromWeb?.val?.type === 'doctoopen_inapp') {
                if (fromWeb?.val?.Url == (homeBaseUrl || '') + "tlyfe/external/services") {
                    navigation?.navigate('Marketplace');
                } else {
                    Linking.openURL(fromWeb?.val?.Url);
                }
            }
        } catch (e) {
            console.log('Error parsing WebView message', e);
        }
    }, [onPress, homeBaseUrl, navigation]);

    const handleIframeMessage = useCallback((event: { origin: string; data: string; }) => {
        if (event.origin === homeBaseUrl) {
            try {
                const fromIframe = JSON.parse(event.data);
                console.log('fromIframe', fromIframe);
                if (fromIframe?.val?.type === 'doctoopen_inapp') {
                    if (fromIframe?.val?.Url === (homeBaseUrl || '') + 'tlyfe/external/services') {
                        navigation?.navigate('Marketplace');
                    } else {
                        Linking.openURL(fromIframe?.val?.Url);
                    }
                }
            } catch (error) {
                console.error('Error parsing message from iframe', error);
            }
        }
    }, [homeBaseUrl, navigation]);

    useEffect(() => {
        if (Platform.OS == "web") {
            const win = typeof window !== 'undefined' ? (window as any) : null;
            const doc = typeof document !== 'undefined' ? (document as any) : null;

            if (!win || !doc) return;

            win.addEventListener('message', handleIframeMessage);

            const handleBlur = () => {
                if (doc.activeElement === iframeRef.current) {
                    onPress?.();
                    setTimeout(() => {
                        win.focus();
                    }, 100);
                }
            };

            win.addEventListener('blur', handleBlur);

            const timer = setTimeout(() => {
                setLoading(false);
            }, 2000);

            return () => {
                win.removeEventListener('message', handleIframeMessage);
                win.removeEventListener('blur', handleBlur);
                clearTimeout(timer);
            };
        }
    }, [onPress, handleIframeMessage]);

    // Custom Skeleton Block component
    const SkeletonBlock = useCallback(({ width, height, style = {} }: { width: any, height: any, style?: any }) => {
        const interpolatedBackground = shimmerAnimation.interpolate({
            inputRange: [0, 0.5, 1],
            outputRange: [
                theme.colors.neutral[200],
                theme.colors.neutral[300],
                theme.colors.neutral[200]
            ],
        });

        return (
            <Animated.View
                style={[
                    {
                        width,
                        height,
                        backgroundColor: interpolatedBackground,
                        borderRadius: 8,
                    },
                    style,
                ]}
            />
        );
    }, [shimmerAnimation, theme.colors]);

    // Custom Skeleton loader component
    const HomeBoxSkeletonLoader = useCallback(() => (
        <View style={[styles(theme.colors, fullWidth).containerStyle, { overflow: 'hidden', padding: 16 }]}>
            <SkeletonBlock
                width={'60%'}
                height={24}
                style={{ marginBottom: 8 }}
            />
            <SkeletonBlock
                width={'40%'}
                height={16}
                style={{ marginBottom: 24 }}
            />

            <SkeletonBlock
                width={'100%'}
                height={120}
                style={{ marginBottom: 16 }}
            />

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
                <SkeletonBlock
                    width={'48%'}
                    height={80}
                />
                <SkeletonBlock
                    width={'48%'}
                    height={80}
                />
            </View>

            {/* <SkeletonBlock
                width={'100%'}
                height={60}
                style={{ marginBottom: 16 }}
            />

            <View style={{ position: 'absolute', bottom: 16, left: 16, right: 16 }}>
                <SkeletonBlock
                    width={'100%'}
                    height={40}
                />
            </View> */}
        </View>
    ), [theme.colors, fullWidth, SkeletonBlock]);

    const containerStyle = useMemo(() => styles(theme.colors, fullWidth).containerStyle, [theme.colors, fullWidth]);

    return (
        <View style={containerStyle}>
            {loading && <HomeBoxSkeletonLoader />}

            {Platform.OS == "web" ? (
                <div onClick={() => console.log("CLICK")} className="rounded" style={{ display: loading ? 'none' : 'block', height: '100%' }}>
                    <iframe
                        ref={iframeRef}
                        width="100%"
                        frameBorder="0"
                        height="100%"
                        src={generatePartnerUrl(homeBaseUrl || '', "PRIMARY", iFrameParams)}
                        title="Home box"
                        onLoad={() => setLoading(false)}
                    >
                    </iframe>
                </div>
            ) : (
                <WebView
                    scalesPageToFit={true}
                    bounces={false}
                    javaScriptEnabled
                    style={{
                        height: "100%",
                        width: '100%',
                        resizeMode: 'cover',
                        flex: 1,
                        borderRadius: 8,
                        opacity: loading ? 0 : 0.99
                    }}
                    injectedJavaScript={`
                        const meta = document.createElement('meta'); 
                        meta.setAttribute('content', 'width=width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'); 
                        meta.setAttribute('name', 'viewport'); 
                        document.getElementsByTagName('head')[0].appendChild(meta);
                        
                        document.addEventListener('click', function() {
                            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'iframe_click' }));
                        });
                        true;
                    `}
                    source={{ uri: generatePartnerUrl(homeBaseUrl || '', "PRIMARY", iFrameParams) }}
                    automaticallyAdjustContentInsets={false}
                    onMessage={handleWebViewMessage}
                    onLoadEnd={() => setLoading(false)}
                />
            )}
        </View>
    );
});


const styles = (colors: any, fullWidth: any) =>
    StyleSheet.create({
        containerStyle: {
            width: getScreenType() == "phone" ? "100%" : fullWidth ? 628 : 428,
            borderRadius: 12,
            shadowColor: '#101828',
            height: 350,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
            backgroundColor: colors.neutral[100],
            alignSelf: 'center',
            // marginTop: 20,
            borderWidth: 1,
            borderColor: colors.neutral[200],
        },
    });

export default ODSHomeIframe;
